import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleRoute from "@/components/RoleRoute";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Contacts from "@/pages/Contacts";
import ContactProfile from "@/pages/ContactProfile";
import Templates from "@/pages/Templates";
import Campaigns from "@/pages/Campaigns";
import CampaignDetail from "@/pages/CampaignDetail";
import Lists from "@/pages/Lists";
import Bills from "@/pages/Bills";
import Notices from "@/pages/Notices";
import VoiceCampaigns from "@/pages/VoiceCampaigns";
import SmartReminders from "@/pages/SmartReminders";
import Conversations from "@/pages/Conversations";
import MessageLogs from "@/pages/MessageLogs";
import Calls from "@/pages/Calls";
import Reports from "@/pages/Reports";
import Providers from "@/pages/Providers";
import Webhooks from "@/pages/Webhooks";
import Team from "@/pages/Team";
import Companies from "@/pages/Companies";
import AuditLogs from "@/pages/AuditLogs";
import Invoices from "@/pages/Invoices";
import Settings from "@/pages/Settings";
import WhatsAppSettings from "@/pages/WhatsAppSettings";
import WhatsAppNumbers from "@/pages/WhatsAppNumbers";
import Inbox from "@/pages/Inbox";
import Wallet from "@/pages/Wallet";
import Billing from "@/pages/Billing";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsConditions from "@/pages/TermsConditions";
import DataDeletion from "@/pages/DataDeletion";
import Verify from "@/pages/Verify";
import Developers from "@/pages/Developers";
import SenderIds from "@/pages/SenderIds";
import DltCompliance from "@/pages/DltCompliance";
import Segments from "@/pages/Segments";
import Lookup from "@/pages/Lookup";
import EmailDomains from "@/pages/EmailDomains";
import { JourneysList, JourneyEditor } from "@/pages/Journeys";
import { OtpPlans, OtpApiKeys, OtpSendTest, OtpLogs } from "@/pages/OtpPages";
import { OtpTemplates, OtpDocs, OtpOverviewV2 as OtpOverview } from "@/pages/OtpPagesV2";
import OtpAdminTenants from "@/pages/OtpAdminTenants";
import Chatbots from "@/pages/Chatbots";
import ChatbotBuilder from "@/pages/ChatbotBuilder";

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* PUBLIC legal pages — accessible without login (required for Meta App review) */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/contacts/:id" element={<ContactProfile />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/campaigns" element={<RoleRoute allow={["super_admin","admin"]}><Campaigns /></RoleRoute>} />
              <Route path="/campaigns/:id" element={<RoleRoute allow={["super_admin","admin"]}><CampaignDetail /></RoleRoute>} />
              <Route path="/lists" element={<RoleRoute allow={["super_admin","admin"]}><Lists /></RoleRoute>} />
              <Route path="/bills" element={<Bills />} />
              <Route path="/notices" element={<RoleRoute allow={["super_admin","admin"]}><Notices /></RoleRoute>} />
              <Route path="/voice-campaigns" element={<VoiceCampaigns />} />
              <Route path="/reminders" element={<RoleRoute allow={["super_admin","admin"]}><SmartReminders /></RoleRoute>} />
              <Route path="/conversations" element={<Conversations />} />
              <Route path="/messages" element={<MessageLogs />} />
              <Route path="/calls" element={<Calls />} />
              <Route path="/reports" element={<RoleRoute allow={["super_admin","admin"]}><Reports /></RoleRoute>} />
              <Route path="/providers" element={<RoleRoute allow={["super_admin","admin"]}><Providers /></RoleRoute>} />
              <Route path="/webhooks" element={<RoleRoute allow={["super_admin","admin"]}><Webhooks /></RoleRoute>} />
              <Route path="/team" element={<RoleRoute allow={["super_admin","admin"]}><Team /></RoleRoute>} />
              <Route path="/companies" element={<RoleRoute allow={["super_admin"]}><Companies /></RoleRoute>} />
              <Route path="/audit-logs" element={<RoleRoute allow={["super_admin","admin"]}><AuditLogs /></RoleRoute>} />
              <Route path="/invoices" element={<RoleRoute allow={["super_admin","admin"]}><Invoices /></RoleRoute>} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/whatsapp-settings" element={<RoleRoute allow={["super_admin","admin"]}><WhatsAppSettings /></RoleRoute>} />
              <Route path="/whatsapp-numbers" element={<RoleRoute allow={["super_admin","admin","manager","agent"]}><WhatsAppNumbers /></RoleRoute>} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/verify" element={<Verify />} />
              <Route path="/developers" element={<RoleRoute allow={["super_admin","admin"]}><Developers /></RoleRoute>} />
              <Route path="/sender-ids" element={<RoleRoute allow={["super_admin","admin"]}><SenderIds /></RoleRoute>} />
              <Route path="/dlt" element={<RoleRoute allow={["super_admin","admin"]}><DltCompliance /></RoleRoute>} />
              <Route path="/segments" element={<RoleRoute allow={["super_admin","admin"]}><Segments /></RoleRoute>} />
              <Route path="/lookup" element={<Lookup />} />
              <Route path="/email-domains" element={<RoleRoute allow={["super_admin","admin"]}><EmailDomains /></RoleRoute>} />
              <Route path="/journeys" element={<RoleRoute allow={["super_admin","admin"]}><JourneysList /></RoleRoute>} />
              <Route path="/journeys/:id" element={<RoleRoute allow={["super_admin","admin"]}><JourneyEditor /></RoleRoute>} />
              <Route path="/otp" element={<OtpOverview />} />
              <Route path="/otp/plans" element={<OtpPlans />} />
              <Route path="/otp/keys" element={<RoleRoute allow={["super_admin","admin"]}><OtpApiKeys /></RoleRoute>} />
              <Route path="/otp/test" element={<RoleRoute allow={["super_admin","admin"]}><OtpSendTest /></RoleRoute>} />
              <Route path="/otp/logs" element={<OtpLogs />} />
              <Route path="/otp/templates" element={<RoleRoute allow={["super_admin","admin"]}><OtpTemplates /></RoleRoute>} />
              <Route path="/otp/docs" element={<OtpDocs />} />
              <Route path="/otp/admin/tenants" element={<RoleRoute allow={["super_admin"]}><OtpAdminTenants /></RoleRoute>} />
              {/* Chatbot module (configurable WhatsApp auto-reply) */}
              <Route path="/chatbots" element={<RoleRoute allow={["super_admin","admin","manager","agent"]}><Chatbots /></RoleRoute>} />
              <Route path="/chatbots/:id/builder" element={<RoleRoute allow={["super_admin","admin","manager"]}><ChatbotBuilder /></RoleRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
